<script setup>
import { computed, ref, watch } from 'vue'
import authService from '@/shared/services/auth'

// helper para paginación
function pageWindow(totalPages, current, span = 5) {
  // Devuelve una lista compacta con gaps: 1 … 4 5 [6] 7 8 … 20
  const items = []
  if (totalPages <= span + 4) {
    for (let i = 1; i <= totalPages; i++) items.push({ key: `p${i}`, num: i, label: i })
    return items
  }
  const add = (num, label = null) => items.push({ key: `p${label ?? num}`, num, label: label ?? num })
  const addGap = (id) => items.push({ key: `g${id}`, isGap: true, label: '…' })

  add(1)
  const start = Math.max(2, current - 2)
  const end = Math.min(totalPages - 1, current + 2)

  if (start > 2) addGap('l')
  for (let i = start; i <= end; i++) add(i)
  if (end < totalPages - 1) addGap('r')

  add(totalPages)
  return items
}

/**
 * Props:
 * - columns: [{ key, label, sortable?: boolean, footer?: 'sum'|'avg'|'count'|fn, format?: (val,row)=>any }]
 * - rows: Array<Record<string, any>>
 * - filterPlaceholder: string
 * - initialSort: { key: string, dir: 'asc'|'desc' }
 * - caseSensitiveFilter: boolean
 * - enablePagination: boolean
 * - perPage: number (tamaño de página inicial)
 * - pageSizes: number[] (opciones del selector)
 */
const props = defineProps({
  columns: { type: Array, required: true },
  rows: { type: Array, required: true },
  filterPlaceholder: { type: String, default: 'Filtrar…' },
  initialSort: { type: Object, default: () => ({ key: '', dir: 'asc' }) },
  caseSensitiveFilter: { type: Boolean, default: false },
  enablePagination: { type: Boolean, default: true },
  perPage: { type: Number, default: 10 },
  pageSizes: { type: Array, default: () => [5, 10, 25, 50, 100] },
  striped: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
  showActions: { type: Boolean, default: false },
  actionsLabel: { type: String, default: 'Acciones' },
  requireEmpresa: { type: Boolean, default: true },
  empresaRequiredMessage: { type: String, default: 'No hay empresa seleccionada. Seleccione una empresa para continuar.' }
})

const emit = defineEmits([
  'update:filter',
  'sort-change',
  'page-change',
  'page-size-change'
])

/* ---------- Empresa seleccionada ---------- */
const hasEmpresa = computed(() => {
  try {
    const empresa = authService.getSelectedEmpresa()
    return !!(empresa && empresa.EmpresaId)
  } catch (e) {
    return false
  }
})
const canShowGrid = computed(() => !props.requireEmpresa || hasEmpresa.value)

/* ---------- Filtro ---------- */
const filterText = ref('')
watch(filterText, v => emit('update:filter', v))

/* ---------- Orden ---------- */
const sortState = ref({
  key: props.initialSort?.key || '',
  dir: props.initialSort?.dir || 'asc'
})
function toggleSort(col) {
  if (!col.sortable) return
  if (sortState.value.key !== col.key) {
    sortState.value = { key: col.key, dir: 'asc' }
  } else {
    sortState.value.dir = sortState.value.dir === 'asc' ? 'desc' : 'asc'
  }
  emit('sort-change', { ...sortState.value })
}

function normalize(val) {
  if (val == null) return ''
  if (typeof val === 'number') return val
  if (typeof val === 'boolean') return val ? 1 : 0
  return String(val)
}

/* ---------- Filtrado ---------- */
const filteredRows = computed(() => {
  const q = props.caseSensitiveFilter ? filterText.value : filterText.value.toLowerCase()
  if (!q) return props.rows
  return props.rows.filter(row =>
    props.columns.some(c => {
      const v = normalize(row[c.key])
      const s = typeof v === 'number' ? String(v) : String(v)
      return (props.caseSensitiveFilter ? s : s.toLowerCase()).includes(q)
    })
  )
})

/* ---------- Orden aplicado ---------- */
const sortedRows = computed(() => {
  const { key, dir } = sortState.value
  if (!key) return filteredRows.value
  const col = props.columns.find(c => c.key === key)
  if (!col) return filteredRows.value

  const arr = [...filteredRows.value]
  arr.sort((a, b) => {
    const va = normalize(a[key])
    const vb = normalize(b[key])
    const na = typeof va === 'number' || (!isNaN(parseFloat(va)) && isFinite(va))
    const nb = typeof vb === 'number' || (!isNaN(parseFloat(vb)) && isFinite(vb))
    let cmp = 0
    if (na && nb) {
      cmp = (parseFloat(va) ?? 0) - (parseFloat(vb) ?? 0)
    } else {
      cmp = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' })
    }
    return dir === 'asc' ? cmp : -cmp
  })
  return arr
})

/* ---------- Paginación ---------- */
const page = ref(1)
const perPage = ref(props.perPage)

const totalRows = computed(() => sortedRows.value.length)
const totalPages = computed(() => {
  if (!props.enablePagination) return 1
  return Math.max(1, Math.ceil(totalRows.value / perPage.value))
})

watch([sortedRows, perPage], () => {
  // Ajusta la página cuando cambian datos o tamaño de página
  if (page.value > totalPages.value) page.value = totalPages.value
  if (page.value < 1) page.value = 1
})

function setPerPage(val) {
  perPage.value = Number(val) || props.perPage
  page.value = 1
  emit('page-size-change', perPage.value)
}

function goTo(p) {
  const target = Math.min(Math.max(1, p), totalPages.value)
  if (target !== page.value) {
    page.value = target
    emit('page-change', target)
  }
}

const fromIdx = computed(() => {
  if (!props.enablePagination) return totalRows.value ? 1 : 0
  return totalRows.value ? (page.value - 1) * perPage.value + 1 : 0
})
const toIdx = computed(() => {
  if (!props.enablePagination) return totalRows.value
  return Math.min(page.value * perPage.value, totalRows.value)
})

const displayedRows = computed(() => {
  if (!props.enablePagination) return sortedRows.value
  const start = (page.value - 1) * perPage.value
  return sortedRows.value.slice(start, start + perPage.value)
})

/* ---------- Pie (agregados) ---------- */
const footerValues = computed(() => {
  const out = {}
  const rows = sortedRows.value // agregados sobre el total filtrado y ordenado
  props.columns.forEach(col => {
    const agg = col.footer
    if (!agg) { out[col.key] = ''; return }
    const values = rows.map(r => {
      const v = r[col.key]
      return typeof v === 'number' ? v : parseFloat(v)
    }).filter(v => !isNaN(v))

    if (typeof agg === 'function') {
      out[col.key] = agg(rows, col.key)
    } else if (agg === 'sum') {
      out[col.key] = values.reduce((s, v) => s + v, 0)
    } else if (agg === 'avg') {
      out[col.key] = values.length ? values.reduce((s, v) => s + v, 0) / values.length : 0
    } else if (agg === 'count') {
      out[col.key] = rows.length
    } else {
      out[col.key] = ''
    }
  })
  return out
})
</script>

<template>
  <div class="dg-wrap">
<!-- Toolbar superior -->
<div class="dg-toolbar">
  <!-- Selector de cantidad de registros -->
  <div class="dg-page-size">
    <label>
      Mostrar
      <select :value="perPage" @change="setPerPage($event.target.value)">
        <option v-for="n in pageSizes" :key="n" :value="n">{{ n }}</option>
      </select>
      registros
    </label>
  </div>

  <!-- Buscador -->
  <div class="dg-right">
    <input
      v-model="filterText"
      class="dg-input"
      :placeholder="filterPlaceholder"
      type="search"
      aria-label="Filtro global"
    />
  </div>
</div>

    <!-- Mensaje de empresa requerida -->
    <div v-if="!canShowGrid" class="dg-table-wrap">
      <div class="alert alert-warning dg-empty">{{ props.empresaRequiredMessage }}</div>
    </div>

    <!-- Tabla -->
    <div v-else class="dg-table-wrap">
      <table class="dg-table">
        <thead>
          <tr>
            <th v-for="col in columns" :key="col.key">
              <button
                class="dg-th-btn"
                :class="{ sortable: col.sortable }"
                @click="toggleSort(col)"
                :aria-sort="sortState.key === col.key ? (sortState.dir === 'asc' ? 'ascending' : 'descending') : 'none'"
              >
                <span>{{ col.label }}</span>
                <span v-if="col.sortable" class="dg-sort">
                  <span v-if="sortState.key !== col.key">⇅</span>
                  <span v-else>{{ sortState.dir === 'asc' ? '▲' : '▼' }}</span>
                </span>
              </button>
            </th>
            <th v-if="showActions" class="dg-actions-header">
              <span>{{ actionsLabel }}</span>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(row, rIdx) in displayedRows" :key="rIdx" :class="{ 'dg-striped': striped && rIdx % 2 === 0, 'dg-compact': compact }">
            <td v-for="col in columns" :key="col.key" :class="{ 'dg-compact': compact }">
              <slot
                :name="`cell-${col.key}`"
                :row="row"
                :value="row[col.key]"
              >
                {{ col.format ? col.format(row[col.key], row) : row[col.key] }}
              </slot>
            </td>
            <td v-if="showActions" class="dg-actions-cell" :class="{ 'dg-compact': compact }">
              <slot
                name="actions"
                :row="row"
                :index="rIdx"
              >
                <!-- Contenido por defecto si no se proporciona slot -->
              </slot>
            </td>
          </tr>
          <tr v-if="displayedRows.length === 0">
            <td :colspan="showActions ? columns.length + 1 : columns.length" class="dg-empty">Sin resultados</td>
          </tr>
        </tbody>

        <tfoot>
          <tr>
            <td v-for="col in columns" :key="col.key" class="dg-foot">
              <slot :name="`foot-${col.key}`" :value="footerValues[col.key]">
                <template v-if="col.footer === 'sum'">Σ {{ footerValues[col.key] }}</template>
                <template v-else-if="col.footer === 'avg'">Prom: {{ footerValues[col.key] }}</template>
                <template v-else-if="col.footer === 'count'"># {{ footerValues[col.key] }}</template>
                <template v-else>{{ footerValues[col.key] }}</template>
              </slot>
            </td>
            <td v-if="showActions" class="dg-foot"></td>
          </tr>
        </tfoot>
      </table>
    </div>

    <!-- Paginación -->
    <div v-if="enablePagination && canShowGrid" class="dg-pagination">
      <div class="dg-page-size">
        <label>
          Mostrar
          <select :value="perPage" @change="setPerPage($event.target.value)">
            <option v-for="n in pageSizes" :key="n" :value="n">{{ n }}</option>
          </select>
          registros
        </label>
      </div>

      <div class="dg-page-info">
        Mostrando {{ fromIdx }} a {{ toIdx }} de {{ totalRows }} registros
      </div>

      <div class="dg-page-buttons" role="navigation" aria-label="Paginación de tabla">
        <button class="dg-btn" :disabled="page<=1" @click="goTo(1)" aria-label="Primera página">⏮</button>
        <button class="dg-btn" :disabled="page<=1" @click="goTo(page-1)" aria-label="Página anterior">◀</button>

        <!-- Números de página compactos -->
        <button
          v-for="p in pageWindow(totalPages, page)"
          :key="p.key"
          class="dg-btn"
          :disabled="p.isGap"
          :aria-current="p.num === page ? 'page' : null"
          :class="{ active: p.num === page, gap: p.isGap }"
          @click="!p.isGap && goTo(p.num)"
        >
          {{ p.label }}
        </button>

        <button class="dg-btn" :disabled="page>=totalPages" @click="goTo(page+1)" aria-label="Página siguiente">▶</button>
        <button class="dg-btn" :disabled="page>=totalPages" @click="goTo(totalPages)" aria-label="Última página">⏭</button>
      </div>
    </div>
  </div>
</template>


<style scoped>
.dg-wrap { display: grid; gap: .75rem; }
.dg-toolbar { display: flex; justify-content: space-between; align-items: center; gap: .5rem; }
.dg-toolbar .dg-right { margin-top: 1rem; }
.dg-input {
  max-width: 260px; width: 100%; padding: .5rem .75rem; border: 1px solid #ddd; border-radius: .5rem;
}
.dg-table-wrap { overflow: auto; border: 1px solid #eee; border-radius: .5rem; }
.dg-table { width: 100%; border-collapse: collapse; font-size: 14px; }
th, td { padding: .5rem .75rem; border-bottom: 1px solid #eee; text-align: left; vertical-align: middle; }
thead th { position: sticky; top: 0; background: #fafafa; z-index: 1; }
tfoot td { position: sticky; bottom: 0; background: #fbfbfb; border-top: 2px solid #f0f0f0; }
.dg-th-btn { all: unset; display: inline-flex; gap: .35rem; align-items: center; cursor: default; }
.dg-th-btn.sortable { cursor: pointer; }
.dg-th-btn.sortable:hover span:first-child { text-decoration: underline; }
.dg-sort { font-size: .8em; opacity: .8; }
.dg-empty { text-align: center; color: #888; padding: 1rem; }
.dg-foot { font-weight: 600; color: #444; }

/* Paginación */
.dg-pagination {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: .5rem;
  margin-top: 1rem;
}
.dg-page-size { justify-self: start; }
.dg-page-info { justify-self: center; color: #555; }
.dg-page-buttons { justify-self: end; display: flex; gap: .25rem; flex-wrap: wrap; }
.dg-btn {
  border: 1px solid #ddd; background: #fff; padding: .35rem .55rem; border-radius: .4rem; cursor: pointer; min-width: 2rem;
}
.dg-btn[disabled] { opacity: .5; cursor: not-allowed; }
.dg-btn.active { background: #0b63e2; color: #fff; border-color: #0b63e2; }
.dg-btn.gap { cursor: default; }

/* Filas alternadas */
.dg-striped { background-color: #f8f9fa; }

/* Filas compactas */
.dg-compact th,
.dg-compact td {
  padding: .25rem .5rem;
}

td.dg-compact {
  padding: .25rem .5rem;
}

/* Columna de acciones */
.dg-actions-header {
  width: 120px;
  text-align: center;
}

.dg-actions-cell {
  text-align: center;
  white-space: nowrap;
}
</style>